import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Lock, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminPilotDashboard from '@/components/AdminPilotDashboard';
import { WestHamRelegationModal } from '@/components/modals/WestHamRelegationModal';
import { authenticate, getAdminSession, setAdminSession, clearAdminSession } from '@/lib/adminAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPilotPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showWestHamModal, setShowWestHamModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const session = getAdminSession();
    if (session) {
      setCurrentUser(session);
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    const user = authenticate(email, password);
    if (user) {
      setAdminSession(user);
      setCurrentUser(user);
      setIsAuthenticated(true);

      axios.post(`${API}/admin/activity`, {
        email: user.email,
        name: user.name,
        role: user.role,
      }).catch(err => console.error('Failed to log activity:', err));

      toast.success(`Welcome back, ${user.name}!`);

      // Easter egg: cheeky popup just for Chris. Shows max 2 times ever (persisted in localStorage).
      if (user.email === 'chris@ref-assist.com') {
        const count = parseInt(localStorage.getItem('refassist_chris_easter_egg_count') || '0', 10);
        if (count < 2) {
          localStorage.setItem('refassist_chris_easter_egg_count', String(count + 1));
          setTimeout(() => setShowWestHamModal(true), 600);
        }
      }
    } else {
      toast.error('Invalid email or password');
    }
  };

  const handleLogout = () => {
    clearAdminSession();
    setIsAuthenticated(false);
    setCurrentUser(null);
    setEmail('');
    setPassword('');
    toast.success('Logged out successfully');
  };
  
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-card border-border/50 p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-accent to-accent/80 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-accent-foreground" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2 font-['Space_Grotesk']">Pilot Applications</h1>
            <p className="text-sm text-muted-foreground">Sign in to view pilot programme applications</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-foreground font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@ref-assist.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-muted/50 border-border/50 focus:border-accent text-foreground h-11"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-foreground font-medium">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-muted/50 border-border/50 focus:border-accent text-foreground h-11 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <Button type="submit" className="w-full bg-accent hover:bg-accent/90 text-accent-foreground h-11">
              Sign In
            </Button>
            
            <Button 
              type="button" 
              variant="ghost" 
              className="w-full"
              onClick={() => navigate('/')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </form>
        </Card>
      </div>
    );
  }
  
  return (
    <>
      <AdminPilotDashboard currentUser={currentUser} onLogout={handleLogout} />
      <WestHamRelegationModal
        open={showWestHamModal}
        onClose={() => setShowWestHamModal(false)}
      />
    </>
  );
}
